import { CogIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import {
  InputType,
  IVisibleProp,
  loanCustomRangeOptionFields,
  NewFileTableTypeOptions,
  NewInputTextOptions,
} from 'config'
import { normalizeCoborrowerRequiredRecord } from 'config/borrowerFieldRequired.util'
import { canAddNewField, InputTypeOfLoans, isCustomFieldKey } from 'config/loan.input.fields.constants'
import { FieldOrigin, ILoanFieldPos, isCustomOrigin, LoanFieldProps } from 'config/loan.input.visibility.type'
import { BorrowerSeperator } from 'pages/LoanApplication/config'
import { useEffect, useMemo, useState } from 'react'
import { Modal, Tooltip } from 'stories/components'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { capitalizeFirstLetter, InputConvert, InputValidate, isEmpty, promptVisibleLogic, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { CoborrowerRequiredByBorrowerFields } from '../CoborrowerRequiredByBorrowerFields'
import { hasCoborrowerRequiredProps, hasRequiredProps, isInputTypeChangableFieldOrigins } from '../inputs'
import { LinkToParty } from '../LinkToParty'
import { convertSelectRangeOptionsToMap } from '../utils'
import {
  defaultInputs,
  editableSelectFields,
  editPermissionFieldOrigins,
  inferActionTypeFromFieldOrigin,
  invisibleFieldsForLinkedField,
  isVisibleAccountTypesFieldOrigins,
  loanContextVisibleLogicAdditionalFieldOrigins,
  lockByFieldOrigins,
  titleLogicFieldOrigins,
  viewOnlyLogicFieldOrigins,
} from './constants'
import { LlpaLlraMode, LlpaLlraSection } from './LlpaLlraSection'
import { SelectOptionsCustomization } from './SelectOptionsCustomization'
import { SelectOptionsCustomRange } from './SelectOptionsCustomRange'
import { StateChoices } from './StateChoices'
import { ILinkedFieldProperties, SyncCustomFieldTo } from './SyncCustomFieldTo'
import { ITitleVisibilityValue, TitleVisibilityModal } from './TitleVisibilityModal'
import { ViewOnlyConditions } from './ViewOnlyConditions'

interface IVisiblePropertyModalProps {
  sectionOptions?: Record<string, string> | null
  data: IVisibleProp
  field: ILoanFieldPos
  inputProps: InputType
  fieldTitles: Record<string, { title: string; fields: Record<string, string> }>
  fieldInputs: InputTypeOfLoans
  actionTypeOptions?: Record<string, string>
  onClose: Function
  properties?: {
    linkedField?: ILinkedFieldProperties
  }
  loanFieldProps: LoanFieldProps
  showAdjustments?: boolean
}

export const VisiblePropertyModal = ({
  sectionOptions = null,
  data: _data,
  field,
  inputProps,
  fieldTitles,
  fieldInputs,
  actionTypeOptions = {},
  onClose,
  properties,
  loanFieldProps,
  showAdjustments = true,
}: IVisiblePropertyModalProps) => {
  const { fieldOrigin, fieldKey, defaultFieldData, defaultPropData } = field

  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [data, setData] = useState({
    ...defaultPropData,
    ..._data,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showError, setShowError] = useState(false)
  const [showTitleVisibilityModal, setShowTitleVisibilityModal] = useState(false)

  const showCustomRangeInput = useMemo(() => loanCustomRangeOptionFields.includes(fieldKey), [fieldKey])
  const isMortgageLates = useMemo(() => fieldKey === 'mortgageLates', [fieldKey])
  const isPPP = useMemo(() => fieldKey === 'prepaymentPenalty', [fieldKey])
  const modalWidthClass = useMemo(() => {
    if (showAdjustments) return isMortgageLates ? 'w-[900px]' : isPPP ? 'w-[1050px]' : 'w-[760px]'
    return isMortgageLates ? 'w-[600px]' : isPPP ? 'w-[800px]' : 'w-144'
  }, [showAdjustments, isMortgageLates, isPPP])
  const isCustomField = useMemo(
    () => !fieldKey || isCustomOrigin(fieldOrigin) || isCustomFieldKey(fieldKey),
    [fieldOrigin, fieldKey],
  )
  const isLinkedField = useMemo(
    () => (isCustomField || properties?.linkedField?.showLinkedField) && data.linkedField,
    [isCustomField, data.linkedField],
  )
  const isLoanStructure = useMemo(() => fieldOrigin === FieldOrigin.LoanStructure, [fieldOrigin])
  const modalTitle = useMemo(() => {
    if (field.title) return field.title
    if (isLinkedField) return 'New Linked Field'
    if (isCustomField) return 'New Custom Field'
    return 'New Field'
  }, [field.title, isCustomField, isLinkedField])

  const isShowType =
    !isLinkedField &&
    ((isInputTypeChangableFieldOrigins(fieldOrigin) &&
      ['text', 'Text', 'ToggleButton', 'togglebutton', 'FileTable', 'filetable', undefined].includes(
        inputProps.inputType,
      )) ||
      isCustomField)

  useEffect(() => {
    if (!data) return

    let newInputs = cloneDeep(inputs)
    if (defaultFieldData) {
      Object.keys(defaultFieldData).forEach((key) => {
        if (!newInputs[key]) return
        newInputs[key].value = defaultFieldData[key]
      })
    }
    if (data.customOptions && (!data.options || (typeof data.options == 'object' && !Array.isArray(data.options)))) {
      data.options = data.customOptions
      delete data.customOptions
    }

    Object.keys(newInputs).forEach((key) => {
      if ((data as any)[key] === undefined) return
      newInputs[key].value = (data as any)[key]
    })

    newInputs.required.visible = hasRequiredProps(fieldOrigin)
    newInputs.required.value = data.required === undefined ? inputProps.required : data.required

    newInputs.coborrowerRequired.visible = hasCoborrowerRequiredProps(fieldOrigin)
    newInputs.coborrowerRequired.value = normalizeCoborrowerRequiredRecord(
      (data as any).coborrowerRequired,
      !!newInputs.required.value,
    ) as any

    if (!data.title) newInputs.title.value = field.title

    if (canAddNewField(fieldOrigin)) newInputs.title.required = true
    if (isShowType) {
      newInputs.inputType.value = capitalizeFirstLetter(data.inputType || inputProps.inputType)
      newInputs.inputType.visible = true
      if (String(newInputs.inputType.value).toLowerCase() === 'filetable') {
        const rawType = (data as any).type ?? (inputProps as any).type
        newInputs.type.value =
          rawType !== undefined && rawType !== null && String(rawType).length ? String(rawType) : ''
      } else {
        newInputs.type.value = data.type || inputProps.type || 'text'
      }
      newInputs.type.visible = true
    } else {
      newInputs.inputType.value = data.inputType || inputProps.inputType || ''
      newInputs.inputType.visible = false
      newInputs.type.visible = false
    }
    if (newInputs.inputType.value == 'ActionButton') {
      newInputs.actionType.value = data.actionType || (inputProps as any).actionType || ''
      newInputs.buttonStyle.value = data.buttonStyle || (inputProps as any).buttonStyle || 'primary'
      newInputs.clickMode.value = data.clickMode || (inputProps as any).clickMode || 'always'
    }
    newInputs.options.visible = inputProps?.inputType == 'Select' && isCustomField

    newInputs.linkToParty.visible = isCustomOrigin(fieldOrigin)
    newInputs.linkToParty.value = data.linkToParty || {}

    newInputs.span.visible = isCustomOrigin(fieldOrigin)
    newInputs.span.value = data.span ?? 1

    if (data.section && sectionOptions && sectionOptions[data.section] === undefined) {
      const newSectionKey = Object.keys(sectionOptions).find((key) => sectionOptions[key] == `Section ${data.section}`)
      if (newSectionKey) newInputs.section.value = newSectionKey
    }

    ;(newInputs.visibleLogic as any).fieldTitles = fieldTitles
    ;(newInputs.visibleLogic as any).fieldInputs = fieldInputs

    setInputs(newInputs)
  }, [])

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    if (key !== 'coborrowerRequired') {
      value = InputConvert(newInputs[key], value)
    }
    if (key === 'coborrowerRequired' && value && typeof value === 'object') {
      newInputs[key].error = undefined
    } else {
      newInputs[key].error = InputValidate({ ...newInputs[key], value })
    }
    newInputs[key].value = value
    if (key === 'inputType') {
      const normalized = capitalizeFirstLetter(value)
      if (String(normalized).toLowerCase() === 'filetable') {
        newInputs.type.value = ''
      } else if (normalized === 'Text') {
        newInputs.type.value = 'text'
      }
    }
    if (key === 'coborrowerRequired' && value && typeof value === 'object' && newInputs.required) {
      newInputs.required.value = value[BorrowerSeperator.BORROWER]
    }
    if (key === 'required' && hasCoborrowerRequiredProps(fieldOrigin) && newInputs.coborrowerRequired) {
      const prev = (inputs as any).coborrowerRequired?.value
      if (typeof prev === 'object' && prev) {
        newInputs.coborrowerRequired.value = { ...prev, [BorrowerSeperator.BORROWER]: value }
      } else {
        newInputs.coborrowerRequired.value = {
          ...normalizeCoborrowerRequiredRecord(prev, !!value),
          [BorrowerSeperator.BORROWER]: value,
        } as any
      }
    }
    setInputs(newInputs)
  }

  const visibleLogicFieldProps = () => {
    const next = cloneDeep(loanFieldProps)
    if (fieldOrigin && fieldKey) {
      if (!next[fieldOrigin]) (next as any)[fieldOrigin] = {}
      ;(next as any)[fieldOrigin][fieldKey] = {
        ...((next as any)[fieldOrigin]?.[fieldKey] || {}),
        ...data,
      }
    }
    return next
  }

  const onSubmit = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    let newData: any = cloneDeep(data) || {}
    Object.keys(inputs).map((key) => {
      const input = newInputs[key]
      if (input.visible === false) return
      if (String(input.inputType).toLowerCase() === 'section') return

      newInputs[key].error = InputValidate({ ...input, value: input.value })
      if (newInputs[key].error?.length) hasError = true
      newData[key] = newInputs[key].value
    })
    delete newData.editPermissionSection
    setInputs(newInputs)

    const newErrors: Record<string, string> = {}
    if (
      showCustomRangeInput &&
      data?.options &&
      Object.keys(convertSelectRangeOptionsToMap(data.options)).length != data.options.length
    ) {
      newErrors['customRangeSelectOptions'] = 'Duplicated option exists.'
      hasError = true
    }
    if (isPPP && data?.options) {
      data.options.forEach((item: any) => {
        if (isEmpty(item?.value) || isEmpty(item?.subValue)) {
          newErrors['customRangeSelectOptions'] = 'Required field exists.'
          hasError = true
        }

        if (!isEmpty(item?.value) && !isEmpty(item?.subValue)) {
          const raw = item.subValue.replace(/[^0-9]/g, '')
          if (raw.length !== (Math.floor(removeComma(item.value) / 12) || 1)) {
            newErrors['customRangeSelectOptions'] = `${item.value} is not matched to ${item.subValue}`
            hasError = true
          }
        }
      })
    }
    setErrors(newErrors)

    data.options &&
      Array.isArray(data.options) &&
      data.options?.map((item: any) => {
        if (typeof item === 'object' && !item.title) hasError = true
      })
    setShowError(true)

    if (hasError) return

    if (inputs.inputType.value == 'ActionButton') {
      if (!newData.actionKey) {
        const base = (newData.title || '').replace(/[^a-zA-Z0-9]/g, '')
        newData.actionKey = base ? `${base.charAt(0).toLowerCase()}${base.slice(1)}` : `action${Date.now()}`
      }
      delete newData.type
      delete newData.defaultValue
      delete newData.options
      delete newData.required
      delete newData.coborrowerRequired
    }

    if (!canAddNewField(fieldOrigin) && (newData.title == field.title || !newData.title)) delete newData.title
    if (newData.type != data.type) delete newData.defaultValue
    if (newData.type === 'dollar') {
      newData.prefix = '$'
      newData.type = 'thousandSep'
    } else if (newData.type === 'percentage') {
      newData.prefix = '%'
      newData.type = 'number'
    } else delete newData.prefix

    if (newData.inputType == 'ToggleButton' && fieldOrigin == FieldOrigin.DeclarationsHMDA) newData.textLeft = true

    if (
      newData.coborrowerRequired &&
      typeof newData.coborrowerRequired === 'object' &&
      (newData.coborrowerRequired as any)[BorrowerSeperator.BORROWER] !== undefined
    ) {
      newData.required = (newData.coborrowerRequired as any)[BorrowerSeperator.BORROWER]
    }

    if (String(newData.inputType || '').toLowerCase() === 'filetable') {
      newData.acceptFileTypes = newData.type === 'fannie34Xml' ? 'text/xml' : ''
    } else {
      delete newData.acceptFileTypes
    }

    if (isCustomField && !isLinkedField && newData.inputType == 'Select') newData.hasDefaultOption = true

    if (data.titleLogic?.length) newData.titleLogic = data.titleLogic
    else delete newData.titleLogic

    onClose(newData)
  }

  const originalTitle = field.title || inputProps.title || ''
  const titleVisibilityValue: ITitleVisibilityValue = {
    title: String(inputs.title?.value || ''),
    titleLogic: data.titleLogic || [],
  }

  const onTitleVisibilityChange = (result: ITitleVisibilityValue) => {
    onChange('title', result.title)
    setData((prev: IVisibleProp) => ({
      ...prev,
      title: result.title,
      titleLogic: result.titleLogic.length ? result.titleLogic : undefined,
    }))
    setShowTitleVisibilityModal(false)
  }

  const isActionButton = inputs.inputType.value == 'ActionButton'
  const isPropertyState = fieldKey === 'propertyState'
  const llpaSectionMode = useMemo<LlpaLlraMode | null>(() => {
    if (!showAdjustments) return null
    // propertyState stores its checklist on `options`; that must not hide LLPA/LLRA.
    if (!!data?.options?.length && !isPropertyState) return null

    const inputType = String(inputs.inputType.value || data.inputType || inputProps.inputType || '').toLowerCase()
    const valueType = String(inputs.type.value || data.type || inputProps.type || '').toLowerCase()
    const selectOptionsEditorVisible =
      inputType === 'select' && (isCustomField || editableSelectFields.includes(fieldKey))

    if (inputType === 'select' && !selectOptionsEditorVisible) return 'select'
    if (
      inputType === 'text' &&
      ['thousandsep', 'thousandsepnodecimal', 'number', 'creditscore', 'dollar', 'percentage'].includes(valueType)
    )
      return 'range'
    if (inputType === 'check' || inputType === 'checkbox') return 'check'
    return null
  }, [
    showAdjustments,
    fieldKey,
    isPropertyState,
    inputs.inputType.value,
    inputs.type.value,
    data.inputType,
    data.type,
    data?.options?.length,
    inputProps,
    isCustomField,
  ])

  return (
    <>
      <Modal
        isOpen
        className={showAdjustments ? 'lg:max-w-[calc(100vw-4rem)]' : ''}
        title={modalTitle}
        titleClass={modalWidthClass}
        titleOkay="Submit"
        onClose={() => onClose()}
        onOk={onSubmit}
      >
        <div className={modalWidthClass}>
          {Object.keys(inputs).map((key, index) => {
            if (key == 'type') {
              const it = inputs.inputType.value
              const isFileTable = String(it).toLowerCase() === 'filetable'
              inputs[key].visible = it === 'Text' || isFileTable
              if (isFileTable) {
                inputs[key].options = NewFileTableTypeOptions
                inputs[key].title = 'Table format'
                ;(inputs[key] as any).hasDefaultOption = false
                ;(inputs[key] as any).allowDefaultOption = true
              } else if (it === 'Text') {
                inputs[key].options = NewInputTextOptions
                inputs[key].title = 'Input Format'
                ;(inputs[key] as any).hasDefaultOption = true
                ;(inputs[key] as any).allowDefaultOption = true
              }
            }
            if (key == 'title' && titleLogicFieldOrigins(fieldOrigin)) {
              inputs[key].additionalElements = (
                <Tooltip message="Title Visibility Logic">
                  <CogIcon
                    className="w-4 h-4 cursor-pointer text-shade-blue"
                    onClick={() => setShowTitleVisibilityModal(true)}
                  />
                </Tooltip>
              ) as any
            }
            if (key == 'defaultValue') {
              inputs[key].visible = ['percentOfLoanAmount'].includes(inputs.type.value)
              if (inputs.type.value == 'percentOfLoanAmount') {
                inputs[key].title = 'Percent of Loan Amount'
              }
            }
            if (['lockByAccountTypes', 'lockByLoanStatus'].includes(key)) {
              inputs[key].visible = lockByFieldOrigins(fieldOrigin)
            }
            if (key == 'viewOnlyLogic') {
              inputs[key].visible = viewOnlyLogicFieldOrigins(fieldOrigin)
            }
            if (['editPermissionSection', 'editPermissionAccountTypes'].includes(key)) {
              inputs[key].visible = editPermissionFieldOrigins(fieldOrigin)
            }
            if (key == 'options')
              inputs.options.visible =
                inputs.inputType.value == 'Select' && (isCustomField || editableSelectFields.includes(fieldKey))
            if (key == 'actionType') {
              ;(inputs[key] as any).options = actionTypeOptions
              inputs[key].visible = isActionButton
              if (isActionButton && !inputs[key].value) inputs[key].value = inferActionTypeFromFieldOrigin(fieldOrigin)
            }
            if (key == 'buttonStyle') {
              inputs[key].visible = isActionButton
              if (isActionButton && !inputs[key].value) inputs[key].value = 'primary'
            }
            if (key == 'clickMode') {
              inputs[key].visible = isActionButton
              if (isActionButton && !inputs[key].value) inputs[key].value = 'always'
            }
            if (isActionButton && ['type', 'options', 'coborrowerRequired', 'required'].includes(key))
              inputs[key].visible = false
            if (isLinkedField && invisibleFieldsForLinkedField.includes(key)) inputs[key].visible = false

            if (inputs[key].visible === false) return null
            if (
              ['visibleAccountTypes', 'visibleLoanTypes'].includes(key) &&
              !isVisibleAccountTypesFieldOrigins(fieldOrigin)
            )
              return null
            if (key == 'section') {
              if (!sectionOptions) return null
              else (inputs[key] as any).options = sectionOptions
            }
            if (key == 'linkToParty')
              (inputs[key] as any).render = (value: any, onChange: Function) => (
                <LinkToParty value={value} onChange={(v: any) => onChange(v)} />
              )
            if (key == 'viewOnlyLogic')
              (inputs[key] as any).render = (value: any, onChange: Function, input: InputType) => (
                <ViewOnlyConditions value={value} onChange={onChange} input={input} />
              )

            if (key === 'coborrowerRequired' && hasCoborrowerRequiredProps(fieldOrigin)) {
              return (
                <div key={index} className="mb-2">
                  <CoborrowerRequiredByBorrowerFields
                    value={inputs.coborrowerRequired.value as any}
                    primaryRequired={!!inputs.required?.value}
                    onChange={(rec) => onChange('coborrowerRequired', rec)}
                  />
                </div>
              )
            }

            if (key == 'options') {
              if (fieldOrigin === FieldOrigin.LoanStructure && showAdjustments) {
                ;(inputs[key] as any).showAdjustments = true
              }
              inputs[key].additionalElements = ((opt: ISelectOption, onChange: Function) => {
                return (
                  <Tooltip message="Visible Logic">
                    <CogIcon
                      className="w-4 h-4 cursor-pointer text-shade-blue"
                      onClick={async () => {
                        const result = await promptVisibleLogic(`Visible Logic for "${inputs.title.value}"`, {
                          fieldTitles,
                          fieldInputs,
                          loanFieldProperties: visibleLogicFieldProps(),
                          value: opt.visibleLogic,
                          hasNotVisible: true,
                          additionalFieldOrigins: loanContextVisibleLogicAdditionalFieldOrigins(),
                        })
                        if (!result) return

                        onChange({
                          visibleLogic: result,
                        })
                      }}
                    />
                  </Tooltip>
                )
              }) as any
            }

            return (
              <div key={index} className="mb-2">
                <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
              </div>
            )
          })}
          <SelectOptionsCustomRange
            data={data}
            errors={errors}
            showError={showError}
            setData={setData}
            fieldKey={fieldKey}
            promptVisibleLogic={async (idx: number) => {
              const result = await promptVisibleLogic(`Visible Logic for "${inputs.title.value}"`, {
                fieldTitles,
                fieldInputs,
                loanFieldProperties: visibleLogicFieldProps(),
                value: data?.options?.[idx]?.visibleLogic,
                hasNotVisible: true,
                additionalFieldOrigins: loanContextVisibleLogicAdditionalFieldOrigins(),
              })
              return result
            }}
            showAdjustments={showAdjustments}
          />
          <SelectOptionsCustomization
            data={data}
            setData={setData}
            fieldKey={fieldKey}
            promptVisibleLogic={async (idx: number) => {
              const result = await promptVisibleLogic(`Visible Logic for "${inputs.title.value}"`, {
                fieldTitles,
                fieldInputs,
                loanFieldProperties: visibleLogicFieldProps(),
                value: (data?.options as any)?.[idx]?.visibleLogic,
                hasNotVisible: true,
                additionalFieldOrigins: loanContextVisibleLogicAdditionalFieldOrigins(),
              })
              return result
            }}
            showAdjustments={showAdjustments}
            isMasterSection={isLoanStructure}
          />
          {isPropertyState && isLoanStructure && <StateChoices data={data} setData={setData} />}
          {llpaSectionMode && isLoanStructure && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <LlpaLlraSection
                fieldKey={fieldKey}
                data={data}
                setData={setData}
                mode={llpaSectionMode}
                showError={showError}
              />
            </div>
          )}
          {isLinkedField && (
            <SyncCustomFieldTo
              data={data}
              setData={setData}
              fieldOrigin={fieldOrigin}
              fieldKey={fieldKey}
              fieldTitles={fieldTitles}
              fieldInputs={fieldInputs}
              properties={properties?.linkedField}
              loanFieldProps={loanFieldProps}
            />
          )}
        </div>
      </Modal>
      <TitleVisibilityModal
        isOpen={showTitleVisibilityModal}
        originalTitle={originalTitle}
        value={titleVisibilityValue}
        fieldTitles={fieldTitles}
        fieldInputs={fieldInputs}
        onClose={() => setShowTitleVisibilityModal(false)}
        onOk={onTitleVisibilityChange}
      />
    </>
  )
}
